import { Alert, AlertColor, Box, Snackbar, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { ReferralSettingsForm } from "../../components/backend/referral";
import {
  useReferralSettings,
  useReferralSettingsMutation,
} from "../../hooks/useReferral";
import { UpdateReferralSettingsRequest } from "../../types/referral";

import AdminPage from "./AdminPage";

const ReferralSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: settingsResponse, isLoading } = useReferralSettings();
  const updateMutation = useReferralSettingsMutation();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({ open: false, message: "", severity: "info" });

  const settings = settingsResponse?.data;

  const handleSave = async (data: UpdateReferralSettingsRequest) => {
    try {
      await updateMutation.mutateAsync(data);
      setSnackbar({
        open: true,
        message: t("admin.referral.settingsSaved"),
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: t("admin.referral.settingsSaveFailed"),
        severity: "error",
      });
    }
  };

  return (
    <AdminPage>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t("admin.referral.referralSettings")}
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : settings ? (
          <ReferralSettingsForm
            settings={settings}
            onSave={handleSave}
            isSaving={updateMutation.isPending}
          />
        ) : (
          <Alert severity="error">{t("apiErrors.SERVER_ERROR")}</Alert>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </AdminPage>
  );
};

export default ReferralSettingsPage;
